import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import useGa4Property from "../hooks/useGa4Property"
import { supabase } from "../supabaseClient"
import { formatNumber } from "../utils/formatters"

const FCR_EVENT_NAMES = [
  "asc_form_submission",
  "asc_comm_submission",
  "asc_voice_submission",
]

export default function GA4PropertyFCR() {
  const navigate = useNavigate()
  const { search } = useLocation()
  const propertyId = new URLSearchParams(search).get("id")
  const [eventAudit, setEventAudit] = useState([])
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditError, setAuditError] = useState(null)
  const {
    property: propInfo,
    loading: propertyLoading,
    error: propertyError,
  } = useGa4Property(propertyId)

  useEffect(() => {
    if (propertyLoading) return

    if (propertyError || !propInfo?.id) {
      setEventAudit([])
      setAuditError(propertyError || null)
      setAuditLoading(false)
      return
    }

    let cancelled = false

    const loadAudit = async () => {
      setAuditLoading(true)
      setAuditError(null)
      try {
        const { data: audit, error } = await supabase
          .from("ga4_event_audit_results")
          .select(
            "event_owner, first_detected, last_detected, event_count, event_details, monthly_breakdown"
          )
          .eq("ga4_property_id", propInfo.id)
          .order("event_owner", { ascending: true })
        if (error) throw error
        if (cancelled) return
        setEventAudit(
          (audit || []).map((row) => ({
            ...row,
            event_details: Array.isArray(row.event_details)
              ? row.event_details
              : [],
            monthly_breakdown: Array.isArray(row.monthly_breakdown)
              ? row.monthly_breakdown
              : [],
          }))
        )
      } catch (err) {
        if (cancelled) return
        console.error("Failed to load GA4 FCR data", err)
        setEventAudit([])
        setAuditError(err)
      } finally {
        if (!cancelled) setAuditLoading(false)
      }
    }

    loadAudit()

    return () => {
      cancelled = true
    }
  }, [propInfo?.id, propertyLoading, propertyError])

  const loading = propertyLoading || auditLoading

  return (
    <div className="page-container">
      <h2>Form Completion Rate (FCR)</h2>
      <button onClick={() => navigate(`/ga4-property?id=${encodeURIComponent(propertyId)}`)}>
        Back
      </button>
      {loading ? (
        <p>Loading...</p>
      ) : propertyError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load property details.
        </p>
      ) : auditError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load FCR data.
        </p>
      ) : (
        <>
          {propInfo ? (
            <div style={{ marginTop: "1rem" }}>
              <h3 style={{ margin: 0 }}>
                {propInfo.property_name}
                <span
                  style={{
                    fontWeight: "normal",
                    fontSize: "0.9em",
                    marginLeft: "0.5rem",
                  }}
                >
                  ({propInfo.property_id})
                </span>
              </h3>
            </div>
          ) : (
            <p style={{ marginTop: "1rem" }}>Property not found.</p>
          )}
          <FCRMonthlyBreakdown data={eventAudit} />
        </>
      )}
    </div>
  )
}

function FCRMonthlyBreakdown({ data }) {
  const [monthFilter, setMonthFilter] = useState("")
  const [formTypeFilter, setFormTypeFilter] = useState("")

  const monthlyData = useMemo(() => {
    const map = new Map()
    for (const entry of data) {
      const breakdown = Array.isArray(entry.monthly_breakdown)
        ? entry.monthly_breakdown
        : []
      for (const month of breakdown) {
        const monthKey = month.yearMonth
        if (!monthKey) continue
        const rawEvents = Array.isArray(month.events) ? month.events : []
        const events = rawEvents
          .map((event) => {
            const eventName = event.eventName || ""
            const normalizedName = eventName.toLowerCase()
            return {
              eventName,
              normalizedName,
              eventCount:
                typeof event.eventCount === "number" ? event.eventCount : null,
              formType: event.formType || "",
              productType: event.productType || "",
            }
          })
          .filter((event) => FCR_EVENT_NAMES.includes(event.normalizedName))
        if (!events.length) continue
        const hasAggregateSessions = Object.prototype.hasOwnProperty.call(
          month,
          "totalSessions"
        )
        const monthEntry = map.get(monthKey) || {
          entries: [],
          totalEvents: 0,
          totalSessions: hasAggregateSessions ? null : 0,
          hasAggregateSessions: hasAggregateSessions,
        }
        if (hasAggregateSessions) {
          monthEntry.hasAggregateSessions = true
        }
        const rawSessionCount =
          typeof month.sessionCount === "number" ? month.sessionCount : null
        const aggregateSessions = hasAggregateSessions
          ? typeof month.totalSessions === "number"
            ? month.totalSessions
            : null
          : rawSessionCount
        const ownerSessionCount = hasAggregateSessions ? null : rawSessionCount
        const totalCount = events.reduce((sum, event) => {
          if (typeof event.eventCount === "number") {
            return sum + event.eventCount
          }
          return sum
        }, 0)
        monthEntry.entries.push({
          owner: entry.event_owner,
          events: events.map(({ normalizedName, ...rest }) => rest),
          sessionCount: ownerSessionCount,
        })
        monthEntry.totalEvents += totalCount
        if (hasAggregateSessions) {
          monthEntry.totalSessions =
            typeof aggregateSessions === "number" ? aggregateSessions : null
        } else if (typeof aggregateSessions === "number") {
          monthEntry.totalSessions += aggregateSessions
        }
        map.set(monthKey, monthEntry)
      }
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([monthKey, info]) => ({
        monthKey,
        entries: info.entries.sort((a, b) => a.owner.localeCompare(b.owner)),
        totalEvents: info.totalEvents,
        totalSessions: info.totalSessions,
        hasAggregateSessions: info.hasAggregateSessions,
      }))
  }, [data])

  const monthOptions = useMemo(() => {
    return monthlyData.map((month) => ({
      value: month.monthKey,
      label: formatYearMonth(month.monthKey),
    }))
  }, [monthlyData])

  const filteredMonthlyData = useMemo(() => {
    const normalizedMonthFilter = monthFilter.trim().toLowerCase()
    const normalizedFormTypeFilter = formTypeFilter.trim().toLowerCase()

    return monthlyData
      .map((month) => {
        const label = formatYearMonth(month.monthKey)
        const monthMatches =
          !normalizedMonthFilter ||
          month.monthKey.toLowerCase().includes(normalizedMonthFilter) ||
          label.toLowerCase().includes(normalizedMonthFilter)
        if (!monthMatches) return null

        const entries = month.entries
          .map((entry) => {
            if (!normalizedFormTypeFilter) return { ...entry }
            const filteredEvents = entry.events.filter((event) => {
              const formType = (event.formType || "").toLowerCase()
              const productType = (event.productType || "").toLowerCase()
              return (
                formType.includes(normalizedFormTypeFilter) ||
                productType.includes(normalizedFormTypeFilter)
              )
            })
            if (!filteredEvents.length) return null
            return {
              ...entry,
              events: filteredEvents,
            }
          })
          .filter(Boolean)

        if (!entries.length) return null

        const displayedEventTotal = entries.reduce((sum, entry) => {
          return (
            sum +
            entry.events.reduce((eventSum, event) => {
              const value =
                typeof event.eventCount === "number" ? event.eventCount : 0
              return eventSum + value
            }, 0)
          )
        }, 0)

        const totalSessions =
          typeof month.totalSessions === "number"
            ? month.totalSessions
            : entries.reduce((sum, entry) => {
                if (typeof entry.sessionCount === "number") {
                  return sum + entry.sessionCount
                }
                return sum
              }, 0)

        return {
          monthKey: month.monthKey,
          label,
          entries,
          displayedEventTotal,
          totalSessions,
          hasAggregateSessions: month.hasAggregateSessions,
        }
      })
      .filter(Boolean)
  }, [monthlyData, monthFilter, formTypeFilter])

  if (!monthlyData.length) {
    return (
      <p style={{ marginTop: "1.5rem" }}>
        No qualifying form submission events were found for this property.
      </p>
    )
  }

  return (
    <div style={{ marginTop: "2rem" }}>
      <h3>Form Completion Detail</h3>
      <p style={{ marginTop: 0, fontStyle: "italic" }}>
        The table below only includes sessions that recorded any of the
        <code style={{ margin: "0 0.25rem" }}>{FCR_EVENT_NAMES.join(", ")}</code>
        events.
      </p>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          marginTop: "0.5rem",
          alignItems: "center",
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <span style={{ fontSize: "0.9rem", fontWeight: 600 }}>Month filter</span>
          <input
            list="ga4-fcr-month-filter-options"
            value={monthFilter}
            onChange={(event) => setMonthFilter(event.target.value)}
            placeholder="Type a month (e.g. 2024-01 or January)"
            style={{ padding: "0.25rem", minWidth: "220px" }}
          />
          <datalist id="ga4-fcr-month-filter-options">
            {monthOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </datalist>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <span style={{ fontSize: "0.9rem", fontWeight: 600 }}>Form/product type filter</span>
          <input
            value={formTypeFilter}
            onChange={(event) => setFormTypeFilter(event.target.value)}
            placeholder="Type part of a form or product type"
            style={{ padding: "0.25rem", minWidth: "220px" }}
          />
        </label>
        {(monthFilter || formTypeFilter) && (
          <button
            type="button"
            onClick={() => {
              setMonthFilter("")
              setFormTypeFilter("")
            }}
            style={{ padding: "0.35rem 0.75rem" }}
          >
            Clear filters
          </button>
        )}
      </div>
      {filteredMonthlyData.length ? (
        filteredMonthlyData.map((month) => (
          <div key={month.monthKey} style={{ marginTop: "1rem" }}>
            <h4 style={{ marginBottom: "0.25rem" }}>{month.label}</h4>
            <p style={{ marginTop: 0, fontStyle: "italic" }}>
              Total submissions: {formatNumber(month.displayedEventTotal)}
              {typeof month.totalSessions === "number" && (
                <> • Sessions: {formatNumber(month.totalSessions)}</>
              )}
            </p>
            <table className="ui-table" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ width: "20%" }}>Event Owner</th>
                  <th style={{ width: "18%" }}>Event Name</th>
                  <th style={{ width: "22%" }}>Form Type</th>
                  <th style={{ width: "20%" }}>Product Type</th>
                  <th style={{ width: "10%" }}>Completions</th>
                  <th style={{ width: "10%" }}>Sessions</th>
                  <th style={{ width: "10%" }}>Completion Rate</th>
                </tr>
              </thead>
              <tbody>
                {month.entries.flatMap((entry) => {
                  const sessionValue =
                    month.hasAggregateSessions &&
                    typeof month.totalSessions === "number"
                      ? month.totalSessions
                      : entry.sessionCount
                  const hasSessionValue =
                    typeof sessionValue === "number" &&
                    Number.isFinite(sessionValue)
                  const events = entry.events.length
                    ? entry.events
                    : [
                        {
                          eventName: FCR_EVENT_NAMES[0],
                          eventCount: null,
                          formType: "",
                          productType: "",
                        },
                      ]
                  return events.map((event) => (
                    <tr
                      key={`${month.monthKey}-${entry.owner}-${event.eventName}-${event.formType || "none"}-${event.productType || "none"}`}
                    >
                      <td>{entry.owner}</td>
                      <td>{event.eventName}</td>
                      <td>{event.formType || "—"}</td>
                      <td>{event.productType || "—"}</td>
                      <td>
                        {typeof event.eventCount === "number"
                          ? formatNumber(event.eventCount)
                          : "—"}
                      </td>
                      <td>
                        {hasSessionValue
                          ? formatNumber(sessionValue)
                          : "—"}
                      </td>
                      <td>
                        {typeof event.eventCount === "number" &&
                        hasSessionValue &&
                        sessionValue > 0
                          ? formatPercent(event.eventCount, sessionValue)
                          : "—"}
                      </td>
                    </tr>
                  ))
                })}
              </tbody>
            </table>
          </div>
        ))
      ) : (
        <p style={{ marginTop: "1rem", fontStyle: "italic" }}>
          No events match the current filters.
        </p>
      )}
    </div>
  )
}

function formatPercent(numerator, denominator) {
  const num = Number(numerator)
  const den = Number(denominator)
  if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) {
    return "—"
  }
  return `${((num / den) * 100).toFixed(1)}%`
}

function formatYearMonth(value) {
  if (!value || typeof value !== "string") return value || "—"
  const parts = value.split("-")
  if (parts.length !== 2) return value
  const [year, month] = parts
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, 1))
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(date)
}
